import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@goaltree/ui/components/field'
import { Input } from '@goaltree/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { authClient } from '@/lib/auth-client'

export const Route = createFileRoute('/auth/forgot-password')({
  head: () => ({
    meta: [{ title: 'Reset password — Goaltree' }],
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [sent, setSent] = useState(false)

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      await authClient.requestPasswordReset(
        { email: value.email, redirectTo: '/auth/reset-password' },
        {
          onSuccess: () => setSent(true),
          onError: (error) => void toast.error(error.error.message || error.error.statusText),
        },
      )
    },
    validators: {
      onSubmit: z.object({
        email: z.email(t('auth.invalidEmail')),
      }),
    },
  })

  if (sent) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-xl font-bold">{t('auth.forgotSentTitle')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.forgotSentPre')}{' '}
            <span className="font-medium text-foreground">{form.getFieldValue('email')}</span>
          </p>
        </div>
        <FieldDescription className="text-center">
          {t('auth.backTo')}{' '}
          <button
            type="button"
            onClick={() => navigate({ to: '/auth/login' })}
            className="cursor-pointer font-medium text-foreground underline underline-offset-4 hover:text-primary-text"
          >
            {t('auth.signIn')}
          </button>
        </FieldDescription>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-1 text-center">
        <h1 className="text-xl font-bold">{t('auth.forgotTitle')}</h1>
        <p className="text-sm text-muted-foreground">{t('auth.forgotSubtitle')}</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <FieldGroup>
          <form.Field name="email">
            {(field) => (
              <Field data-invalid={field.state.meta.errors.length > 0 || undefined}>
                <FieldLabel htmlFor={field.name}>{t('auth.emailLabel')}</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  className="h-11 rounded-lg px-4 text-sm"
                />
                <FieldError errors={field.state.meta.errors} />
              </Field>
            )}
          </form.Field>

          <form.Subscribe
            selector={(s) => ({ canSubmit: s.canSubmit, isSubmitting: s.isSubmitting })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Field>
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('auth.sending')}
                    </>
                  ) : (
                    t('auth.forgotSendLink')
                  )}
                </button>
                <FieldDescription className="text-center">
                  {t('auth.rememberIt')}{' '}
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/auth/login' })}
                    className="cursor-pointer font-medium text-foreground underline underline-offset-4 hover:text-primary-text"
                  >
                    {t('auth.signIn')}
                  </button>
                </FieldDescription>
              </Field>
            )}
          </form.Subscribe>
        </FieldGroup>
      </form>
    </div>
  )
}
